import * as Yup from 'yup';
import {
  min1Value,
  requiredErrorMessage,
  totalWeightRequiredError,
} from 'constants/constants';
import {
  ScopeThreeData,
  ScopeWasteType,
} from 'models/ActivityDataEntryScopes/scopeThree.model';
import {
  createRequiredTest,
  isAnyFieldFilled,
} from 'shared/utils/scopeThreeValidation';
import { wasteTypeError } from 'constants/scopeValidationConstants';
import { maxFourDecimalsTest } from 'views/ActivityDataEntry/ScopeOneViewComponent/validationSchema';
import { roundingTheFloatNumber } from 'shared/utils/roundingTheFloatNumber';

interface TestContextExtended {
  from: {
    value: ScopeThreeData;
  }[];
}

export const uniqueIndexValidator = (
  array: ScopeWasteType[],
  currentItem: ScopeWasteType
): boolean => {
  if (!currentItem?.wasteTypeId || !currentItem?.treatmentMethod) return true;

  const currentLabel = `${currentItem.wasteTypeId} - ${currentItem.treatmentMethod}`;

  return array.filter(item =>
    item?.wasteTypeId &&
    item?.treatmentMethod &&
    `${item.wasteTypeId} - ${item.treatmentMethod}` === currentLabel
  ).length <= 1;
};
export const validateWasteSum = (
  scopeWasteType: ScopeWasteType[],
  sum: number
): boolean => {
  const hasNonZeroWaste = scopeWasteType?.some(
    type => type?.totalWasteProduced && Number(type.totalWasteProduced) > 0
  );

  if (!sum && !hasNonZeroWaste) return true;

  const sumOfWasteTypes = scopeWasteType?.reduce(
    (acc, type) => acc + (type ? Number(type?.totalWasteProduced) || 0 : 0),
    0
  );
  
  const roundedSum = roundingTheFloatNumber(sumOfWasteTypes || 0, 4)
  const roundedValue = roundingTheFloatNumber(sum|| 0, 4)

  return roundedSum === roundedValue;
};

// Validation schema with utility functions
export const scopeThreeCateogy5ValidationSchema = (isPF?: boolean) => {
  return Yup.object().shape({
    totalWasteProduced: maxFourDecimalsTest.test(
      'wasteProducedSum',
      'The total waste produced should match the sum of all recorded waste entries.',
      function (value) {
        return validateWasteSum(
          this.parent.scopeWasteType as ScopeWasteType[],
          Number(value)
        );
      }
    ),

    productsTotalWeight: Yup.string().when([], {
      is: () => isPF,
      then: maxFourDecimalsTest
        .required(totalWeightRequiredError)
        .test('min1', min1Value, value => Number(value) >= 1),
      otherwise: maxFourDecimalsTest.notRequired(),
    }),
    totalWasteWater: maxFourDecimalsTest,

    scopeWasteType: Yup.array().of(
      Yup.object().shape({
        wasteTypeId: createRequiredTest(
          'wasteTypeId',
          requiredErrorMessage
        ).test('uniqueIndexNotAllowed', wasteTypeError, function (this) {
          const presentThis = this as Yup.TestContext & TestContextExtended
          const array = presentThis?.from?.[1]?.value.scopeWasteType as ScopeWasteType[];
          const currentItem = presentThis?.from?.[0]?.value as ScopeWasteType;
          return uniqueIndexValidator(array, currentItem);
        }),
        treatmentMethod: createRequiredTest(
          'treatmentMethod',
          requiredErrorMessage
        ).test('uniqueIndexNotAllowed', wasteTypeError, function (this) {
          const presentThis = this as Yup.TestContext & TestContextExtended
          const array = presentThis?.from?.[1]?.value.scopeWasteType as ScopeWasteType[];
          const currentItem = presentThis?.from?.[0]?.value as ScopeWasteType;
          return uniqueIndexValidator(array, currentItem);
        }),
        totalWasteProduced: maxFourDecimalsTest
          .test(
            'totalWasteProducedRequired',
            requiredErrorMessage,
            function (value) {
              const { wasteTypeId, treatmentMethod } = this
                .parent as ScopeWasteType;
              if (
                isAnyFieldFilled({ wasteTypeId, treatmentMethod }) &&
                !value
              ) {
                return this.createError({ message: requiredErrorMessage });
              }
              return true;
            }
          )
          .test(
            'positiveWasteProduced',
            'Total Waste Produced should be greater than 0',
            value => value == null || Number(value) > 0
          )
          .test(
            'totalWasteProducedShouldEqualtoWasteProduced',
            'Sum of waste type should equal total waste produced',
            function () {
              const presentThis = this as Yup.TestContext & TestContextExtended
              const wasteTypes = presentThis?.from?.[1]?.value.scopeWasteType as ScopeWasteType[];
              const totalWasteProducedValue = Number(presentThis?.from?.[1]
                  ?.value.totalWasteProduced
              );
              return validateWasteSum(wasteTypes, totalWasteProducedValue);
            }
          ),
      })
    ),
  });
};
